import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Icon,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import { Col, Row } from '@cuemath/comps';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { usePostEvalSpecialistPauseCancel } from 'actions/eval-reporting';
import { describeInterval, formatIstDate } from '../common/constants';
import { MonoText } from '../common/filters';

const ContextCard = styled(Col)`
  background: ${COLORS.WILD_SAND};
  border-radius: 8px;
`;

const ContextRow = styled(Row)`
  padding: 12px 16px;
`;

const ContextRowTight = styled(Row)`
  padding: 0 16px 12px;
`;

const IdList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CancelPause = ({ target, closeModal, onCancelled }) => {
  const [cancelError, setCancelError] = React.useState(null);
  const [cancelResult, setCancelResult] = React.useState(null);

  const { specialist, interval } = target;
  const leaveUsageIds = React.useMemo(
    () => interval.leave_usage_ids || [],
    [interval.leave_usage_ids],
  );

  const { post: postCancel, isProcessing: isCancelling } =
    usePostEvalSpecialistPauseCancel({
      onComplete: (error, data) => {
        if (error) {
          setCancelError(error || 'Could not cancel the pause.');

          return;
        }
        setCancelError(null);
        setCancelResult(data || {});
      },
    });

  const handleCancel = React.useCallback(() => {
    setCancelError(null);
    postCancel({ leave_usage_ids: leaveUsageIds }, specialist.teacher_id);
  }, [postCancel, leaveUsageIds, specialist.teacher_id]);

  const handleDone = React.useCallback(() => {
    if (cancelResult && onCancelled) onCancelled();
    closeModal();
  }, [cancelResult, onCancelled, closeModal]);

  const deletedIds = (cancelResult || {}).cancelled || [];
  const missedIds = (cancelResult || {}).skipped || [];

  const renderSuccess = () => (
    <Segment>
      {deletedIds.length ? (
        <Text variant="body1" color={COLORS.DARK_GREEN}>
          <Icon name="check circle" /> Cancelled {deletedIds.length} of{' '}
          {leaveUsageIds.length} pause record
          {leaveUsageIds.length === 1 ? '' : 's'}.
        </Text>
      ) : (
        <Text variant="body1" color={COLORS.RUSTY_RED}>
          <Icon name="info circle" /> Nothing was cancelled.
        </Text>
      )}
      {missedIds.length > 0 && (
        <>
          <Separator height={12} transparent />
          <Text variant="body3" color={COLORS.RUSTY_RED}>
            These records were not cancelled — they may already have been removed. The
            specialist can still be blocked at this time:
          </Text>
          <Separator height={4} transparent />
          <IdList>
            {missedIds.map(id => (
              <MonoText key={id}>{id}</MonoText>
            ))}
          </IdList>
        </>
      )}
      {deletedIds.length > 0 && !missedIds.length && (
        <>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            The specialist is bookable again in this window.
          </Text>
        </>
      )}
    </Segment>
  );

  const renderConfirm = () => (
    <>
      <Text variant="body2">
        Cancel this pause and make the specialist bookable again in this window?
      </Text>
      <Separator height={12} transparent />
      <Text variant="body3" color={COLORS.GRAY}>
        Pause record{leaveUsageIds.length === 1 ? '' : 's'} to cancel:
      </Text>
      <Separator height={4} transparent />
      <IdList>
        {leaveUsageIds.map(id => (
          <MonoText key={id}>{id}</MonoText>
        ))}
      </IdList>
    </>
  );

  const renderActions = () => {
    if (cancelResult) {
      return (
        <Button color="black" onClick={handleDone}>
          Done
        </Button>
      );
    }

    return (
      <>
        <Button basic onClick={closeModal}>
          Keep Pause
        </Button>
        <Button
          color="black"
          loading={isCancelling}
          disabled={isCancelling || !leaveUsageIds.length}
          onClick={handleCancel}
        >
          Cancel Pause
        </Button>
      </>
    );
  };

  return (
    <Modal open size="small" onClose={closeModal}>
      <ModalHeader>Cancel a pause</ModalHeader>
      <ModalContent>
        <ModalDescription>
          <ContextCard gutter={0}>
            <ContextRow gutter={0}>
              <Text variant="body2">{specialist.teacher_name || 'Unnamed specialist'}</Text>
            </ContextRow>
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                {specialist.teacher_id}
              </Text>
            </ContextRowTight>
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                {formatIstDate(interval.date)} · {describeInterval(interval)}
              </Text>
            </ContextRowTight>
          </ContextCard>

          <Separator height={16} transparent />
          {cancelResult ? renderSuccess() : renderConfirm()}

          {cancelError && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {cancelError}
              </Text>
            </>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>{renderActions()}</ModalActions>
    </Modal>
  );
};

export default CancelPause;
